import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  alpha,
  useTheme,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
  AttachMoney as AttachMoneyIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";

/**
 * Component to display receiving and checking progress for OUTPUT items
 *
 * Props:
 * - progress: ReceivingProgressDTO object
 * - materialUnitCost: computed material unit cost (Rs per unit)
 * - totalCost: materialUnitCost × received qty
 * - effectiveMaterials: array of selected category input item rows
 */
function ReceivingProgressCard({ progress, materialUnitCost, totalCost, effectiveMaterials, calcSteps, expectedQty, isCategoryOutput }) {
  const theme = useTheme();
  const [effectiveDialogOpen, setEffectiveDialogOpen] = useState(false);

  if (!progress) return null;

  const {
    importName,
    orderedQty,
    totalReceivedQty,
    totalCheckedQty,
    totalDamagedQty,
    pendingCheckQty,
    receivingProgress,
    isFullyReceived,
    isFullyChecked,
    totalReceiveBatches,
    checkedBatches,
    pendingCheckBatches,
    reconstructionAttempts,
  } = progress;

  const hasEffectiveMaterials = isCategoryOutput;

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 2,
          mb: 2,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight="600">
              {importName}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {hasEffectiveMaterials && (
                <Tooltip title="View Effective Materials">
                  <IconButton
                    size="small"
                    onClick={() => setEffectiveDialogOpen(true)}
                    sx={{
                      color: "secondary.main",
                      bgcolor: alpha(theme.palette.secondary.main, 0.08),
                      "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.16) },
                      width: 24,
                      height: 24,
                    }}
                  >
                    <CategoryIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              {isFullyReceived && (
                <Chip
                  label="Fully Received"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
              {isFullyChecked && (
                <Chip
                  label="Fully Checked"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
            </Box>
          </Box>

          {/* Quantities */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Ordered
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {orderedQty?.toFixed(0) || 0}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Received
                </Typography>
                <Typography variant="h6" fontWeight="600" color="primary.main">
                  {totalReceivedQty?.toFixed(0) || 0}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Good
                </Typography>
                <Typography variant="h6" fontWeight="600" color="success.main">
                  {totalCheckedQty?.toFixed(0) || 0}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Damaged
                </Typography>
                <Typography variant="h6" fontWeight="600" color="error.main">
                  {totalDamagedQty?.toFixed(0) || 0}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Receiving Progress
              </Typography>
              <Typography variant="caption" fontWeight="600">
                {receivingProgress?.toFixed(1) || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(receivingProgress || 0, 100)}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
          </Box>

          {/* Batch Information */}
          <Divider sx={{ my: 1.5 }} />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocalShippingIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {totalReceiveBatches || 0} batches received
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {checkedBatches || 0} batches checked
                </Typography>
              </Box>
            </Grid>
            {pendingCheckBatches > 0 && (
              <Grid item xs={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <WarningIcon sx={{ fontSize: 14, color: "warning.main" }} />
                  <Typography variant="caption" color="warning.main">
                    {pendingCheckBatches} pending check ({pendingCheckQty?.toFixed(0)} units)
                  </Typography>
                </Box>
              </Grid>
            )}
            {reconstructionAttempts > 0 && (
              <Grid item xs={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <BuildIcon sx={{ fontSize: 14, color: "info.main" }} />
                  <Typography variant="caption" color="info.main">
                    {reconstructionAttempts} reconstruction(s)
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Material Cost Summary */}
          {(materialUnitCost != null || totalCost != null) && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={2}>
                {materialUnitCost != null && (
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.warning.main, 0.07),
                      }}
                    >
                      <AttachMoneyIcon sx={{ fontSize: 14, color: "warning.dark" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Material Unit Cost
                        </Typography>
                        <Typography variant="caption" fontWeight="700" color="warning.dark">
                          Rs {materialUnitCost.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {totalCost != null && (
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.success.main, 0.07),
                      }}
                    >
                      <AttachMoneyIcon sx={{ fontSize: 14, color: "success.dark" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Total Material Cost
                        </Typography>
                        <Typography variant="caption" fontWeight="700" color="success.dark">
                          Rs {totalCost.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* Effective Materials View Dialog */}
      <Dialog
        open={effectiveDialogOpen}
        onClose={() => setEffectiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CategoryIcon color="secondary" />
              <Typography variant="h6">Effective Materials</Typography>
            </Box>
            <IconButton onClick={() => setEffectiveDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Output item: <strong>{importName}</strong>
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.07) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Conversion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {effectiveMaterials?.map((item, idx) => (
                  <TableRow key={item.importId ?? idx} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {item.rawItemName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.brand}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {parseFloat(item.qty || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.orderUnit || item.unitType}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: 18 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {parseFloat(item.itemUintsPerOrderUnit || 1).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEffectiveDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ReceivingProgressCard;
